/**
 * Auth bootstrap for the SPA.
 *
 * Storage: app JWT in localStorage under `auth.token`. We don't try to be
 * clever with cookies — the API is same-origin behind the FastAPI static
 * fallback, and there's no CSRF surface for a Bearer-token API. Sign-out
 * just clears the key. The server keeps the token short-lived (12h) because
 * any script on the page can read localStorage.
 *
 * Every storage access goes through `storageGet`/`storageSet`/`storageRemove`:
 * browsers throw on storage access when it is blocked (private modes, disabled
 * site data, sandboxed frames). A blocked store reads as "no session" — the
 * user is sent to sign in — instead of crashing the app.
 *
 * Two kinds of session share this storage: STAFF (AllCode, Google on the
 * staff domain) and CUSTOMER (a company login — email + password, or Google on
 * an invited email). `AuthUser.kind` routes them: staff use the consultant app,
 * customers only `/portal` (their own company). A stored user from before
 * `kind` existed is staff.
 *
 * Token lifecycle:
 *   1. User clicks Google button → Google posts ID token to onCredential().
 *   2. We exchange it at POST /api/auth/google for the app JWT.
 *   3. Every API call includes Authorization: Bearer <jwt>.
 *   4. On any 401, clearToken() + redirect to /login.
 */

const TOKEN_KEY = 'auth.token'
const USER_KEY = 'auth.user'

export type UserKind = 'staff' | 'customer'

export interface AuthUser {
  email: string
  name: string
  exp: number
  /** Absent on sessions stored before customer accounts existed → staff. */
  kind?: UserKind
  company_slug?: string | null
  company_name?: string | null
}

export interface AuthConfig {
  google_client_id: string
  allowed_domain: string
  dev_auth_enabled: boolean
  configured: boolean
  password_login_enabled?: boolean
}

/** The backend's LoginResponse (every sign-in endpoint returns it). */
interface LoginResponse {
  token: string
  expires_at: number
  email: string
  name: string
  kind?: UserKind
  company_slug?: string | null
  company_name?: string | null
}

function storageGet(store: () => Storage, key: string): string | null {
  try {
    return store().getItem(key)
  } catch {
    return null
  }
}

function storageSet(store: () => Storage, key: string, value: string): void {
  try {
    store().setItem(key, value)
  } catch {
    // Blocked or full storage: the session just won't survive a reload.
  }
}

function storageRemove(store: () => Storage, key: string): void {
  try {
    store().removeItem(key)
  } catch {
    // Nothing stored that we could reach, so nothing to clear.
  }
}

const local = () => window.localStorage
const session = () => window.sessionStorage

export function getToken(): string | null {
  return storageGet(local, TOKEN_KEY)
}

export function getUser(): AuthUser | null {
  const raw = storageGet(local, USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

export function setSession(token: string, user: AuthUser): void {
  storageSet(local, TOKEN_KEY, token)
  storageSet(local, USER_KEY, JSON.stringify(user))
}

export function userKind(user: AuthUser | null = getUser()): UserKind {
  return user?.kind === 'customer' ? 'customer' : 'staff'
}

/** Where a signed-in user belongs: the company list for staff, the portal
 *  for customers. */
export function homeFor(user: AuthUser | null = getUser()): string {
  return userKind(user) === 'customer' ? '/portal' : '/'
}

/** Whether `path` is somewhere this user may land after sign-in — a customer
 *  never returns into the staff app (and staff never into the portal). */
export function returnAllowedFor(path: string, user: AuthUser | null = getUser()): boolean {
  const portal = path === '/portal' || path.startsWith('/portal/') || path.startsWith('/portal?')
  return userKind(user) === 'customer' ? portal : !portal
}

/** Store the session a sign-in endpoint returned. */
function startSession(body: LoginResponse): AuthUser {
  const user: AuthUser = {
    email: body.email,
    name: body.name,
    exp: body.expires_at,
    kind: body.kind ?? 'staff',
    company_slug: body.company_slug ?? null,
    company_name: body.company_name ?? null,
  }
  setSession(body.token, user)
  return user
}

async function postJson(url: string, payload: unknown, fallback: string): Promise<Response> {
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ detail: fallback }))
    const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message
    throw new Error(detail || fallback)
  }
  return resp
}

export function clearSession(): void {
  storageRemove(local, TOKEN_KEY)
  storageRemove(local, USER_KEY)
}

/** True when we have a token whose `exp` claim hasn't passed. We trust the
 * server to reject anything signed wrong; this is just a UX guard to avoid
 * a bounce through 401. */
export function isAuthenticated(): boolean {
  const user = getUser()
  const token = getToken()
  if (!user || !token) return false
  return user.exp * 1000 > Date.now()
}

/** How long a fresh app JWT must have left for us to let the consultant start
 * work. A single intake/proposal page can take well over an hour; we don't want
 * someone to begin editing on a token that dies mid-form. */
export const REAUTH_BUFFER_SECONDS = 60 * 60 // 1 hour

/** Seconds left before the stored token's `exp`. `null` when there's no session.
 * Negative once it has already expired. */
export function secondsUntilExpiry(): number | null {
  const user = getUser()
  const token = getToken()
  if (!user || !token) return null
  return user.exp - Math.floor(Date.now() / 1000)
}

/** True when the session is missing/expired OR will expire within `bufferSeconds`
 * (default 1h). Checked on page load to force a fresh sign-in *before* the user
 * starts a long form, instead of losing their work to a mid-edit 401. */
export function needsReauthSoon(bufferSeconds: number = REAUTH_BUFFER_SECONDS): boolean {
  const remaining = secondsUntilExpiry()
  return remaining === null || remaining < bufferSeconds
}

/** Exchange Google ID token for an app JWT. */
export async function exchangeGoogleCredential(credential: string): Promise<AuthUser> {
  const resp = await postJson('/api/auth/google', { credential }, 'Sign-in failed.')
  return startSession((await resp.json()) as LoginResponse)
}

/** Customer email + password sign-in. */
export async function passwordSignIn(email: string, password: string): Promise<AuthUser> {
  const resp = await postJson('/api/auth/password', { email, password }, 'Sign-in failed.')
  return startSession((await resp.json()) as LoginResponse)
}

/** Ask for a reset link. The server answers the same whether or not the
 *  address has an account. */
export async function requestPasswordReset(email: string): Promise<void> {
  await postJson('/api/auth/password/forgot', { email }, 'Could not send the reset email.')
}

export interface PasswordTokenInfo {
  valid: boolean
  purpose?: 'invite' | 'reset'
  email?: string
  name?: string
  company_name?: string
}

export async function inspectPasswordToken(token: string): Promise<PasswordTokenInfo> {
  const resp = await postJson('/api/auth/password/inspect', { token }, 'Could not check the link.')
  return (await resp.json()) as PasswordTokenInfo
}

/** Redeem an invite/reset link: sets the password and signs the user in. */
export async function completePasswordReset(token: string, password: string): Promise<AuthUser> {
  const resp = await postJson('/api/auth/password/reset', { token, password }, 'Could not set the password.')
  return startSession((await resp.json()) as LoginResponse)
}

/** Two-step dev sign-in: ask the fake IdP to mint an identity token, then
 * exchange that token for an app JWT via the same code path the Google flow
 * uses on the backend. Only works when DEV_AUTH_ENABLED=true on the server.
 *
 * Caller-supplied `name` is optional; the backend defaults to the email. */
export async function devSignIn(email: string, name?: string): Promise<AuthUser> {
  const issue = await fetch('/api/auth/dev/issue', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, name }),
  })
  if (!issue.ok) {
    const body = await issue.json().catch(() => ({ detail: 'Dev sign-in unavailable.' }))
    throw new Error(body.detail || 'Dev sign-in unavailable.')
  }
  const { credential } = (await issue.json()) as { credential: string }

  const exchange = await fetch('/api/auth/dev', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  })
  if (!exchange.ok) {
    const body = await exchange.json().catch(() => ({ detail: 'Dev sign-in failed.' }))
    throw new Error(body.detail || 'Dev sign-in failed.')
  }
  return startSession((await exchange.json()) as LoginResponse)
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const resp = await fetch('/api/auth/config')
  if (!resp.ok) {
    throw new Error('Failed to load auth config.')
  }
  return (await resp.json()) as AuthConfig
}

/** Hard redirect to /login, preserving the current path so we can bounce
 * back after sign-in. */
export function redirectToLogin(): void {
  const here = window.location.pathname + window.location.search
  if (window.location.pathname !== '/login') {
    storageSet(session, 'auth.returnTo', here)
    window.location.href = '/login'
  }
}
